/* eslint-disable react/jsx-no-bind */
/**
 * @file 项目积分流水面板
 */
import {useEffect, useState, useRef, useCallback} from 'react';
import {Table} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import styles from './TransactionPanel.module.less';
import Tabs from '@/components/common/Tabs';
import {
    PROJECT_TRANSACTIONS_PAGE_SIZE,
    trimProjectTransactionsTabToFirstPage,
    useProjectTransactions,
} from '@/hooks/points/useProjectTransactions';
import type {TransactionRecord, ProjectTransactionsTab} from '@/services/points/interface';
import type {ProjectId} from '@/types';
import {useModalStore} from '@/store/useModalStore';
import loadingIcon from '@/assets/icons/loading.gif';

const EmptyImg =
    'https://now.bdstatic.com/stash/v1/01e9009/mangaflow-fe/9896fde/empty/empty_bg.png';

const TableEmpty = ({text}: {text: string}) => (
    <div className={styles.tableEmpty}>
        <img src={EmptyImg} alt="表格空态图片" className={styles.emptyImg} />
        <div className={styles.emptyText}>{text}</div>
    </div>
);

/** 列表最后一行占位，用于在 tbody 内展示底部加载动画（随内容滚动） */
type TableRow = TransactionRecord & {isSpinnerRow?: boolean};

const SPINNER_ROW: TableRow = {
    nickname: '',
    avatar: '',
    bizType: '',
    amount: 0,
    createTime: '',
    isSpinnerRow: true,
};

/** 与 TransactionPanel.module.less 中 :global 选择器一致，避免 CSS Module 类名与 tbody 内选择器对不上 */
const TX_SPINNER_ROW_CLASS = 'mangaflow-points-txSpinnerRow';
const TX_COL_TIME_CLASS = 'mangaflow-points-txColTime';
const TX_COL_AMOUNT_CLASS = 'mangaflow-points-txColAmount';

const TAB_ITEMS = [
    {key: '0', label: '全部'},
    {key: '1', label: '获取'},
    {key: '2', label: '消耗'},
];

const columns: ColumnsType<TableRow> = [
    {
        title: '成员',
        dataIndex: 'nickname',
        width: '25%',
        render: (_, record) => {
            if (record.isSpinnerRow) {
                return {
                    children: (
                        <div className={styles.spinnerWrapper}>
                            <div className={styles.spinner} />
                        </div>
                    ),
                    props: {colSpan: 4},
                };
            }
            return <span className={styles.userCell}>{record.nickname}</span>;
        },
    },
    {
        title: '类型',
        dataIndex: 'bizType',
        width: '25%',
        render: (v: string, record) => {
            if (record.isSpinnerRow) {
                return {children: null, props: {colSpan: 0}};
            }
            return v;
        },
    },
    {
        title: '时间',
        dataIndex: 'createTime',
        className: TX_COL_TIME_CLASS,
        width: '25%',
        render: (v: string, record) => {
            if (record.isSpinnerRow) {
                return {children: null, props: {colSpan: 0}};
            }
            return v;
        },
    },
    {
        title: '积分',
        dataIndex: 'amount',
        className: TX_COL_AMOUNT_CLASS,
        width: '25%',
        render: (amount: number, record) => {
            if (record.isSpinnerRow) {
                return {children: null, props: {colSpan: 0}};
            }
            const isPositive = amount > 0;
            return (
                <span className={isPositive ? styles.amountPositive : styles.amountNegative}>
                    <span className={styles.prefix}>{isPositive ? '+' : '-'}</span>
                    {Math.abs(amount)}
                </span>
            );
        },
    },
];

interface TransactionPanelProps {
    projectId?: ProjectId;
    resetSignal?: number;
}

const TransactionPanel: React.FC<TransactionPanelProps> = ({projectId, resetSignal = 0}) => {
    const [activeTab, setActiveTab] = useState<ProjectTransactionsTab>(0);
    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const pointsModalOpen = useModalStore((s) => !!s.pointsModalPayload);

    const {
        transactions,
        isLoading,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
        refetch,
    } = useProjectTransactions(projectId, PROJECT_TRANSACTIONS_PAGE_SIZE, activeTab);

    /** 第 1 页 refetch 进行中（含打开弹窗时的显式刷新），完成前不加载后续页 */
    const isPage1Refreshing = isFetching && !isFetchingNextPage;

    const tableData: TableRow[] =
        isFetchingNextPage && transactions.length > 0
            ? [...transactions, SPINNER_ROW]
            : transactions;

    const getTableBody = useCallback(
        () => tableWrapperRef.current?.querySelector<HTMLElement>('.ant-table-tbody'),
        [],
    );

    const handleTabChange = (key: string) => {
        // 先滚顶再切 tab，避免沿用上一 tab 的滚动位置在 trim 后误触发加载
        getTableBody()?.scrollTo({top: 0});
        setActiveTab(Number(key) as ProjectTransactionsTab);
    };

    // 进入（开弹窗 / 切 tab / 切项目 / 提取成功）：滚顶 → trim 到第 1 页 → 显式 refetch
    useEffect(() => {
        if (!projectId) {
            return () => {};
        }

        getTableBody()?.scrollTo({top: 0});
        trimProjectTransactionsTabToFirstPage(projectId, PROJECT_TRANSACTIONS_PAGE_SIZE, activeTab);
        void refetch();

        return () => {
            trimProjectTransactionsTabToFirstPage(
                projectId,
                PROJECT_TRANSACTIONS_PAGE_SIZE,
                activeTab,
            );
        };
    }, [projectId, activeTab, resetSignal, refetch, getTableBody]);

    // 监听 tbody 的 scroll 事件，距底 100px 时加载下一页
    useEffect(() => {
        const body = getTableBody();
        if (!body) {
            return () => {};
        }

        const handleScroll = () => {
            if (!pointsModalOpen) {
                return;
            }

            const {scrollTop, scrollHeight, clientHeight} = body;
            if (
                scrollHeight - scrollTop - clientHeight < 100 &&
                hasNextPage &&
                !isFetchingNextPage &&
                !isPage1Refreshing
            ) {
                void fetchNextPage();
            }
        };

        body.addEventListener('scroll', handleScroll, {passive: true});
        return () => {
            body.removeEventListener('scroll', handleScroll);
        };
    }, [
        pointsModalOpen,
        hasNextPage,
        isFetchingNextPage,
        isPage1Refreshing,
        fetchNextPage,
        getTableBody,
    ]);

    return (
        <div className={styles.container}>
            <Tabs
                activeKey={String(activeTab)}
                onChange={handleTabChange}
                items={TAB_ITEMS}
                className={styles.tabs}
                classNames={{
                    item: styles.tabItem,
                }}
            />
            <div className={styles.tableWrapper} ref={tableWrapperRef}>
                {projectId ? (
                    <Table<TableRow>
                        tableLayout="fixed"
                        className={styles.table}
                        columns={columns}
                        dataSource={tableData}
                        loading={{
                            spinning: isLoading,
                            indicator: (
                                <img
                                    src={loadingIcon}
                                    alt=""
                                    className={styles.tableLoadingIcon}
                                    aria-hidden="true"
                                />
                            ),
                        }}
                        rowKey={(record, index) =>
                            record.isSpinnerRow ? '__spinner__' : String(index)
                        }
                        onRow={(record) =>
                            record.isSpinnerRow ? {className: TX_SPINNER_ROW_CLASS} : {}
                        }
                        size="small"
                        pagination={false}
                        locale={{
                            emptyText: isLoading ? (
                                <span className={styles.tableEmptyHidden} />
                            ) : (
                                <TableEmpty text="暂无项目积分" />
                            ),
                        }}
                    />
                ) : (
                    <TableEmpty text="暂无项目" />
                )}
            </div>
        </div>
    );
};

export default TransactionPanel;
